import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Map } from "lucide-react";
import { useLang } from "../i18n";
import { useAuth } from "../context/AuthContext";
import { api, formatApiError, trackStage, trackEvent } from "../api";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";

export default function AuthPage() {
  const { t } = useLang();
  const { login, register, verifyMfa } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [mode, setMode] = useState(searchParams.get("mode") === "register" ? "register" : "login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [mfaToken, setMfaToken] = useState(null);
  const [mfaCode, setMfaCode] = useState("");
  const rt = searchParams.get("returnTo");
  const returnTo = rt && rt.startsWith("/") && !rt.startsWith("//") ? rt : null;

  // CTA-originated registration: count the registration START once per session
  // (aggregate counter only; the pending-save flag is anonymous sessionStorage).
  useEffect(() => {
    try {
      if (mode === "register" && sessionStorage.getItem("pg_pending_save")
          && !sessionStorage.getItem("pg_reg_start_fired")) {
        sessionStorage.setItem("pg_reg_start_fired", "1");
        trackEvent("save_cta_reg_start");
      }
    } catch { /* private mode */ }
  }, [mode]);
  const afterAuth = () => {
    if (returnTo) api.post("/analytics/event", { event: "login_prompt_conversion" }).catch(() => {});
    try {
      const demoRef = sessionStorage.getItem("pg_demo_ref");
      if (demoRef) {
        api.post("/analytics/event", { event: "demo_conversion", ref: demoRef }).catch(() => {});
        sessionStorage.removeItem("pg_demo_ref");
      }
    } catch { /* private mode */ }
    navigate(returnTo || "/panel");
  };

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      if (mode === "login") {
        const res = await login(email, password);
        if (res?.mfa_required) {
          setMfaToken(res.mfa_token);
          setLoading(false);
          return;
        }
      } else {
        await register(email, password, name);
        trackStage("funnel_register");
        try {
          if (sessionStorage.getItem("pg_pending_save")) trackEvent("save_cta_reg_complete");
        } catch { /* private mode */ }
      }
      afterAuth();
    } catch (err) {
      setError(formatApiError(err));
    }
    setLoading(false);
  };

  const submitMfa = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await verifyMfa(mfaToken, mfaCode);
      afterAuth();
    } catch (err) {
      setError(formatApiError(err));
    }
    setLoading(false);
  };

  return (
    <div data-testid="auth-page" className="mx-auto flex max-w-md flex-col px-4 py-16">
      <div className="rounded-2xl border bg-white p-8 shadow-sm">
        <div className="flex items-center gap-2">
          <span className="flex h-9 w-9 items-center justify-center rounded-md bg-[#DC143C] text-white">
            <Map size={18} />
          </span>
          <span className="font-heading text-xl font-extrabold">Plan<span className="text-[#DC143C]">Gmina</span></span>
        </div>
        <h1 className="mt-6 font-heading text-2xl font-bold">
          {mfaToken ? t("auth.mfa.title") : mode === "login" ? t("auth.login.title") : t("auth.register.title")}
        </h1>

        {mfaToken ? (
          <form onSubmit={submitMfa} className="mt-6 space-y-4">
            <div>
              <Label htmlFor="mfa-code">{t("account.mfa.code")}</Label>
              <Input
                id="mfa-code"
                data-testid="auth-mfa-code-input"
                value={mfaCode}
                onChange={(e) => setMfaCode(e.target.value)}
                required
                inputMode="numeric"
                autoComplete="one-time-code"
                className="mt-1.5"
              />
            </div>
            {error && (
              <p data-testid="auth-error-message" className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
                {error}
              </p>
            )}
            <Button
              data-testid="auth-mfa-submit-button"
              type="submit"
              disabled={loading}
              className="h-11 w-full bg-[#DC143C] text-white hover:bg-[#B91C1C]"
            >
              {loading ? t("common.loading") : t("auth.mfa.submit")}
            </Button>
          </form>
        ) : (
        <form onSubmit={submit} className="mt-6 space-y-4">
          {mode === "register" && (
            <div>
              <Label htmlFor="auth-name">{t("auth.name")}</Label>
              <Input
                id="auth-name"
                data-testid="auth-name-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                className="mt-1.5"
              />
            </div>
          )}
          <div>
            <Label htmlFor="auth-email">{t("auth.email")}</Label>
            <Input
              id="auth-email"
              data-testid="auth-email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="mt-1.5"
            />
          </div>
          <div>
            <Label htmlFor="auth-password">{t("auth.password")}</Label>
            <Input
              id="auth-password"
              data-testid="auth-password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={8}
              className="mt-1.5"
            />
          </div>

          {error && (
            <p data-testid="auth-error-message" className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
              {error}
            </p>
          )}

          <Button
            data-testid="auth-submit-button"
            type="submit"
            disabled={loading}
            className="h-11 w-full bg-[#DC143C] text-white hover:bg-[#B91C1C]"
          >
            {loading ? t("common.loading") : mode === "login" ? t("auth.login.submit") : t("auth.register.submit")}
          </Button>
        </form>
        )}

        <button
          data-testid="auth-mode-toggle"
          onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(null); }}
          className="mt-4 w-full text-center text-sm font-medium text-[#DC143C] hover:underline"
        >
          {mode === "login" ? t("auth.toRegister") : t("auth.toLogin")}
        </button>
      </div>
    </div>
  );
}
